import {Avatar} from '@/components/ui/avatar/Avatar'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    AlertTriangleIcon,
    LogOut01Icon,
    MessageChatSquareIcon,
    User01Icon,
    UserCheck01Icon,
    UserX01Icon
} from '@/components/ui/icon/Icon'
import {useAuthStore, usePermissions} from '@/features/auth/store/store'
import {useImpersonationRemainingTime} from '@/features/auth/components/impersonation-expiry-timer/useImpersonationRemainingTime'
import {useTranslation} from '@/translations/i18n'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {useMemo} from 'react'
import {Divider} from '@/components/ui/divider/Divider'
import {logout} from '@/features/auth/store/actions'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {
    StyledAccountInfo,
    StyledAccountMenu,
    StyledImpersonationButton,
    StyledImpersonationCountdown,
    StyledMenuItem,
    StyledTruncatedText
} from './style'
import {AccountModal} from '@/features/account/components/account-modal/AccountModal'
import {useAccountStore} from '@/features/account/store/useAccountStore'
import {useBugReporterStore} from '@/features/bug-reporter/store/useBugReporterStore'
import {useInternalConversationsStore} from '@/features/conversations/store/useInternalConversationsStore'
import {Text} from '@/components/ui/text/Text'

export const AccountMenu = () => {
    const {t} = useTranslation()

    const user = useAuthStore(state => state.user)
    const impersonating = useAuthStore(state => state.impersonating)
    const stopImpersonation = useAuthStore(state => state.stopImpersonation)
    const remainingTime = useImpersonationRemainingTime()
    const isAccountModalOpened = useAccountStore(state => state.isAccountModalOpened)
    const setIsAccountModalOpened = useAccountStore(state => state.setIsAccountModalOpened)
    const openBugReporter = useBugReporterStore(state => state.open)
    const openInternalConversations = useInternalConversationsStore(state => state.open)
    const permissions = usePermissions()

    const userFullName = useMemo(() => `${user?.firstName} ${user?.lastName}`, [user])

    const handleStopImpersonation = () => {
        stopImpersonation()
        window.location.reload()
    }

    if (!user) return <Skeleton height={26} width={100} />

    return (
        <>
            {isAccountModalOpened && user && <AccountModal user={user} width={'880px'} />}
            <Flexbox gap={3} align="center">
                <MenuProvider>
                    <MenuButton
                        render={
                            impersonating ? (
                                // The icon carries the state for anyone who can't rely on the warning colour;
                                // the label it replaces survives as this button's accessible name.
                                <StyledImpersonationButton
                                    className="print-hidden"
                                    size="sm"
                                    variant="secondary"
                                    shape="roundedRectangle"
                                    aria-label={`${t('impersonation:indicator_label')}: ${userFullName}`}
                                >
                                    <UserCheck01Icon size={15} />
                                    <Text size="textXs" palette="warning" weight="500" shade="700">
                                        {userFullName}
                                    </Text>
                                    {remainingTime && (
                                        <StyledImpersonationCountdown
                                            size="textXs"
                                            palette="warning"
                                            weight="400"
                                            shade="600"
                                        >
                                            {remainingTime}
                                        </StyledImpersonationCountdown>
                                    )}
                                </StyledImpersonationButton>
                            ) : (
                                <Button className="print-hidden" size="sm" variant="secondary" shape="roundedRectangle">
                                    <Avatar name={userFullName} imageUrl={user?.avatarUrl ?? '-'} size={'xs'} />
                                    <Text size="textXs" palette="neutral" weight="500" shade="600">
                                        {userFullName}
                                    </Text>
                                </Button>
                            )
                        }
                    />
                    <StyledAccountMenu gutter={8}>
                        <StyledMenuItem
                            disabled
                            render={
                                <Flexbox gap={3} align={'center'} fullWidth>
                                    <Avatar name={userFullName} imageUrl={user?.avatarUrl ?? '-'} size={'sm'} />
                                    {/* align="stretch": a column flex item defaults to shrink-to-fit width,
                                        so without it the text never has a bounded width to ellipsize against. */}
                                    <StyledAccountInfo direction={'column'} align={'stretch'} fullWidth>
                                        <StyledTruncatedText size="textSm" palette="neutral" weight="500" shade="black">
                                            {userFullName}
                                        </StyledTruncatedText>
                                        <StyledTruncatedText size="textXs" palette="neutral" shade="600">
                                            {user?.email}
                                        </StyledTruncatedText>
                                    </StyledAccountInfo>
                                </Flexbox>
                            }
                        />
                        <Divider direction={'horizontal'} width={1} />
                        <StyledMenuItem
                            render={
                                <Flexbox gap={2} align={'center'} onClick={() => setIsAccountModalOpened(true)}>
                                    <User01Icon size={15} />
                                    {t('commons:your_account')}
                                </Flexbox>
                            }
                        />
                        {permissions?.conversations?.read && (
                            <>
                                <Divider direction={'horizontal'} width={1} />
                                <StyledMenuItem
                                    render={
                                        <Flexbox gap={2} align={'center'} onClick={() => openInternalConversations()}>
                                            <MessageChatSquareIcon size={15} />
                                            {t('conversations:internal_title')}
                                        </Flexbox>
                                    }
                                />
                            </>
                        )}
                        <Divider direction={'horizontal'} width={1} />
                        <StyledMenuItem
                            render={
                                <Flexbox gap={2} align={'center'} onClick={openBugReporter}>
                                    {/* Same icon as the navbar's bug-report trigger, and distinct
                                        from the conversations entry above */}
                                    <AlertTriangleIcon size={15} />
                                    Segnala un problema
                                </Flexbox>
                            }
                        />
                        {impersonating && (
                            <>
                                <Divider direction={'horizontal'} width={1} />
                                <StyledMenuItem
                                    render={
                                        <Flexbox gap={2} align={'center'} onClick={handleStopImpersonation}>
                                            <UserX01Icon size={15} />
                                            {t('impersonation:stop_impersonating')}
                                        </Flexbox>
                                    }
                                />
                            </>
                        )}
                        <Divider direction={'horizontal'} width={1} />
                        <StyledMenuItem
                            render={
                                <Flexbox gap={2} align={'center'} onClick={logout}>
                                    <LogOut01Icon size={15} />
                                    {t('commons:logout')}
                                </Flexbox>
                            }
                        />
                    </StyledAccountMenu>
                </MenuProvider>
            </Flexbox>
        </>
    )
}
